// D3-1 / D3-2 / D3-3 / D3-4 — THE SUPPORT VIEW, CLIENT SIDE.
//
// Its own file for the reason every owner surface since D2 has had one: App.tsx is at the R8 ceiling
// and an owner feature pays for its own lines rather than borrowing against them.
//
// THREE THINGS THIS COMPONENT DELIBERATELY DOES NOT DO, because each is how a support screen fails:
//
//   1. IT NEVER DECIDES WHETHER IT IS UNLOCKED. `supportDoorState` in src/supportView.js derives that
//      from the server's own status answer, and the server re-checks the ticket on every single
//      request regardless. A screen that decides for itself always drifts the same direction.
//   2. IT NEVER COMPOSES THE WALLS. The forbidden list comes DOWN with the payload and is printed
//      through the shared `wallsSentence`. The dashboard cannot promise a wall the server is not
//      holding, because it is not the one holding the list.
//   3. IT NEVER RENDERS A REPORT ITSELF. `renderReport` is handed in from App.tsx and is literally
//      `ReportBody` — the SAME component the member's own page renders from. That is D3-1's whole
//      diagnostic value: a summary of a report cannot show her what looks broken about it.
//
// READ-ONLY IS PROVED BY THE VERB, NOT BY HIDING BUTTONS. The only non-GET fetches in this file are
// the two that manage HER OWN passcode. There is no verb here that writes a member's data.
//
// R78 — **WHAT THIS PANEL SAID WHEN IT COULD NOT READ THE DOOR, AND IT IS THE WORST INSTANCE OF
// R73's SHAPE THIS SWEEP HAS FOUND, BECAUSE THE INITIAL STATE IS NOT AN EMPTY LIST BUT A FORM.**
// `status` starts at `null` and was only ever assigned inside `if (d?.ok)`, and the door was read as
// `supportDoorState(status || {})` — which answers "unset". So a refused, dropped or not-yet-answered
// status read drew the FIRST-TIME SETUP FORM under the sentence "No support passcode is set yet."
// **She fills it in, saves, and the server answers "Your current passcode is not right." — over a
// form that never asked her for one.** The fourth state lives in `src/supportView.js` now
// (`supportDoorView`), because "we do not know yet" is not one of this door's three.
//
// And the second half is R47's count: this file already knew that a 403 saying "Not found." means
// the whole dashboard is closed while a 403 saying `locked: true` means only the ticket expired — it
// says so in a comment — and it acted on it at ONE of its three reads. All three go through
// `src/lib/support-read.ts` now, which is the one place that distinction lives.
//
// The two POSTS are deliberately untouched: they already read their own answers, and D3's
// read-only-proved-by-the-verb law is measured by counting `method: "POST"` in this file
// (`scripts/d3-support-client.mjs`). Moving them onto a transport would have made a privacy law
// harder to see for no gain this round could point at.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  supportDoorView, supportDoorViewCopy, wallsSentence,
  SUPPORT_VIEW_LABEL, LEDGER_NOTE, READ_ONLY_NOTE, PASSCODE_UNREAD,
} from "../supportView.js";
import { loadSupportData, supportReadNote } from "../lib/support-read";

type Status = { passcodeSet: boolean; unlocked: boolean; message?: string };
type SupportReport = { id: string; type: string; title?: string; createdAt?: string | null };
type MaskedProfile = { name: string; birthYear: string; birthCity: string; birthCountry: string; masked: boolean; maskNote: string };
type Payload = {
  member: { id: string; email: string };
  profile: MaskedProfile;
  reports: SupportReport[];
  walls: string[];
};

export function OwnerSupportView({ memberId, onDenied, renderReport, extra }: {
  memberId: string;
  onDenied: () => void;
  renderReport: (data: unknown) => ReactNode;
  // D3-6/D3-7 — the usage page mounts HERE, inside the `open` state, rather than beside this panel.
  // That is deliberate: it means there is no second copy of "am I unlocked" anywhere in the
  // dashboard, so a usage page cannot render against a door this panel believes is shut.
  extra?: ReactNode;
}) {
  const [status, setStatus] = useState<Status | null>(null);
  // TWO FACTS, TWO STATES (R66): what the DOOR read said, and what the last act or open said. One
  // state doing both means re-reading the door erases the account of what just happened at it.
  const [doorNote, setDoorNote] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [entry, setEntry] = useState("");
  const [rotating, setRotating] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [payload, setPayload] = useState<Payload | null>(null);
  const [open, setOpen] = useState<unknown>(null);
  const [openId, setOpenId] = useState("");

  const readStatus = useCallback(async () => {
    const answer = await loadSupportData<Status>("/api/owner/support/status");
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    // **THE DOOR IS ONLY EVER SET FROM AN ANSWER THAT LANDED, AND A FAILED READ LEAVES IT UNKNOWN
    // RATHER THAN SETTING IT TO ANYTHING** — which is what turned a dropped request into a
    // first-time setup form. Held by position (R52).
    if (answer.ok) setStatus({ passcodeSet: answer.data.passcodeSet, unlocked: answer.data.unlocked, message: answer.data.message });
    setDoorNote(supportReadNote(answer, PASSCODE_UNREAD));
  }, [onDenied]);

  useEffect(() => { void readStatus(); }, [readStatus]);
  // A different member means a different view: nothing from the last one may linger on screen.
  useEffect(() => { setPayload(null); setOpen(null); setOpenId(""); }, [memberId]);

  const state = supportDoorView(status);
  const copy = supportDoorViewCopy(status);

  async function unlock() {
    setBusy(true); setNote("");
    try {
      const res = await fetch("/api/owner/support/unlock", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ passcode: entry }),
      });
      const d = await res.json().catch(() => null);
      // A wrong passcode and a stranger both answer 403. They are told apart by WHICH refusal came
      // back, not by the status code: "Not found." is requireOwner's one answer for anyone who is not
      // her, and it is the only 403 here that means the whole dashboard is closed.
      if (res.status === 403 && d?.message === "Not found.") { onDenied(); return; }
      if (d?.ok) { setEntry(""); setNote(""); readStatus(); }
      else setNote(d?.message || "That could not be checked.");
    } catch { setNote("That could not be checked."); }
    finally { setBusy(false); }
  }

  async function savePasscode() {
    setBusy(true); setNote("");
    try {
      const res = await fetch("/api/owner/support/passcode", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ current, next }),
      });
      const d = await res.json().catch(() => null);
      setNote(d?.message || (d?.ok ? "Saved." : "That could not be saved."));
      if (d?.ok) { setCurrent(""); setNext(""); setRotating(false); setPayload(null); readStatus(); }
    } catch { setNote("That could not be saved."); }
    finally { setBusy(false); }
  }

  // D3-1 — open the member's own outputs. GET only, and every open writes a ledger row on the server
  // before the answer is sent, which is why this can be a plain read with no bookkeeping of its own.
  async function openReports() {
    setBusy(true); setNote("");
    const answer = await loadSupportData<Payload>(`/api/owner/support/member/${encodeURIComponent(memberId)}`);
    setBusy(false);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (answer.ok) { setPayload(answer.data); return; }
    // An expired ticket is not a failure to report, it is a door that shut: re-read it, and the
    // passcode form comes back with its own sentence rather than a line about this open.
    if (answer.kind === "locked") { setStatus(null); void readStatus(); return; }
    setNote(supportReadNote(answer, "That could not be opened."));
  }

  async function openReport(id: string) {
    setBusy(true); setNote(""); setOpen(null); setOpenId(id);
    const answer = await loadSupportData<{ report?: { data?: unknown } }>(
      `/api/owner/support/member/${encodeURIComponent(memberId)}/report/${encodeURIComponent(id)}`);
    setBusy(false);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (!answer.ok && answer.kind === "locked") { setOpenId(""); setStatus(null); void readStatus(); return; }
    // **A 200 CARRYING NO REPORT IS A REFUSAL** (R18/R53's presence guard). It used to run
    // `setOpen(d.report?.data ?? d.report)` on any `ok`, so an answer with no report set `undefined`
    // — she taps "Read as member" on a member's reading and the screen goes back to the list with
    // nothing said, which is R25's absent-control argument applied to an absent RESULT.
    const body = answer.ok ? (answer.data.report?.data ?? answer.data.report) : undefined;
    if (answer.ok && body !== undefined && body !== null) { setOpen(body); return; }
    setOpenId("");
    setNote(answer.ok ? "That reading could not be opened." : supportReadNote(answer, "That could not be opened."));
  }

  return (
    <div className="owner-support" data-support-state={state}>
      <h3 className="owner-detail__sub">{copy.heading}</h3>

      {/* The state's own sentence, so she is never guessing which door she is at. It steps aside once
          a member's view is actually open, because the panel then prints the read-only sentence
          itself beside the walls — measured on the capture, the same line appeared twice. */}
      {/* An unread door prints the house's "still asking" line; once the read has FAILED, the note
          supersedes it, because a line saying we are checking is exactly wrong about a check that is
          over (R65's note-supersedes-the-freshness-label, one screen along). */}
      {payload || doorNote ? null : <p className="owner-page__note owner-support__msg">{copy.message}</p>}
      {doorNote ? <p className="owner-page__note owner-support__note owner-fresh--failed" role="status">{doorNote}</p> : null}
      {note ? <p className="owner-page__note owner-support__note owner-fresh--failed" role="status">{note}</p> : null}

      {state === "unset" ? (
        <div className="owner-support__form">
          <label className="owner-grant-form__field">
            <span>New passcode (at least six characters)</span>
            <input className="owner-search" type="password" autoComplete="new-password" value={next}
              onChange={(e) => setNext(e.target.value)} aria-label="New support passcode" />
          </label>
          <button type="button" className="button" disabled={busy || next.length < 6} onClick={() => { void savePasscode(); }}>
            {copy.action}
          </button>
        </div>
      ) : null}

      {state === "locked" ? (
        <div className="owner-support__form">
          <label className="owner-grant-form__field">
            <span>Support passcode</span>
            <input className="owner-search" type="password" autoComplete="off" value={entry}
              onChange={(e) => setEntry(e.target.value)} aria-label="Support passcode"
              onKeyDown={(e) => { if (e.key === "Enter" && entry) void unlock(); }} />
          </label>
          <button type="button" className="button" disabled={busy || !entry} onClick={() => { void unlock(); }}>
            {copy.action}
          </button>
          <p className="owner-page__note">{LEDGER_NOTE}</p>
        </div>
      ) : null}

      {state === "open" ? (
        <div className="owner-support__open">
          {!payload ? (
            <>
              <button type="button" className="button" disabled={busy} onClick={() => { void openReports(); }}>
                {SUPPORT_VIEW_LABEL}
              </button>
              {extra}
              <p className="owner-page__note">{LEDGER_NOTE}</p>
            </>
          ) : (
            <>
              {/* THE WAY BACK. Without it the reports view was a one-way door: opening it hid the
                  usage page (which mounts in `extra`) with no control to bring it back, so the second
                  door existed and could not be reached once the first one had been used. */}
              <button type="button" className="owner-link owner-support__back" onClick={() => { setPayload(null); setOpen(null); setOpenId(""); }}>
                ← Back to this member's doors
              </button>

              {/* D3-2 — the masked profile pane. It says out loud that it is masked. */}
              <div className="owner-support__profile">
                <p className="owner-support__who"><strong>{payload.profile.name || payload.member.email}</strong></p>
                <p className="owner-page__note">
                  Born {payload.profile.birthYear || "year unknown"}
                  {payload.profile.birthCity ? `, ${payload.profile.birthCity}` : ""}
                  {payload.profile.birthCountry ? `, ${payload.profile.birthCountry}` : ""}
                </p>
                <p className="owner-page__note owner-support__mask">{payload.profile.maskNote}</p>
              </div>

              {/* The walls, printed from the SERVER'S list through the shared law. */}
              <p className="owner-page__note owner-support__walls">{wallsSentence(payload.walls)}</p>
              <p className="owner-page__note owner-support__readonly">{READ_ONLY_NOTE}</p>

              {/* THE REPORT ITSELF, through the member's own renderer. Not a summary of it.
                  IT REPLACES THE LIST RATHER THAN APPENDING BELOW IT — the same D5-2 lesson a third
                  time: measured with 25 readings listed, an opened reading rendered off the bottom of
                  the panel and she would have had to scroll past every row to find what she tapped. */}
              {open ? (
                <div className="owner-support__report" data-support-open={openId}>
                  <button type="button" className="owner-link" onClick={() => { setOpen(null); setOpenId(""); }}>
                    ← Back to their readings
                  </button>
                  {renderReport(open)}
                </div>
              ) : payload.reports.length ? (
                <ul className="owner-bars owner-support__list">
                  {payload.reports.map((r) => (
                    <li key={r.id} data-support-report={r.id}>
                      <span className="owner-bars__label">{r.title || r.type}</span>
                      <button type="button" className="owner-link" disabled={busy} onClick={() => { void openReport(r.id); }}>
                        {busy && openId === r.id ? "Opening…" : "Read as member"}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p className="owner-page__note">They have no saved readings.</p>}
            </>
          )}
        </div>
      ) : null}

      {/* D3-3 — changeable from the dashboard. A secret that needs a redeploy to rotate is a secret
          nobody rotates. Rotating does not silently unlock this session: the server clears the ticket. */}
      {state !== "unset" ? (
        <div className="owner-support__rotate">
          <button type="button" className="owner-link" onClick={() => setRotating((v) => !v)}>
            {rotating ? "Never mind" : "Change support passcode"}
          </button>
          {rotating ? (
            <div className="owner-support__form">
              <label className="owner-grant-form__field">
                <span>Current passcode</span>
                <input className="owner-search" type="password" autoComplete="off" value={current}
                  onChange={(e) => setCurrent(e.target.value)} aria-label="Current support passcode" />
              </label>
              <label className="owner-grant-form__field">
                <span>New passcode (at least six characters)</span>
                <input className="owner-search" type="password" autoComplete="new-password" value={next}
                  onChange={(e) => setNext(e.target.value)} aria-label="New support passcode" />
              </label>
              <button type="button" className="button" disabled={busy || next.length < 6 || !current} onClick={() => { void savePasscode(); }}>
                Save passcode
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
